import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faXmark, faLink, faPhone, faComment, faCopy, faPencilAlt } from '@fortawesome/free-solid-svg-icons';

// Button type limits
const BUTTON_TYPE_LIMITS = {
  URL: 2,      // Visit website - 2 buttons max
  PHONE_NUMBER: 1,    // Call phone - 1 button max
  COPY_CODE: 1,     // Copy offer code - 1 button max
  QUICK_REPLY: 3     // Quick reply - 3 buttons max
};

// Maximum total buttons allowed
const MAX_TOTAL_BUTTONS = 3;

function Templatefooter({ footermessage, buttons = [], setFieldValue, advancedOptions, buttonType = 'reply', onButtonTypeChange, templateCategory }) {
  const [footerText, setFooterText] = useState(footermessage || '');
  const [selectedButtonType, setSelectedButtonType] = useState(buttonType);
  const [showButtonForm, setShowButtonForm] = useState(false);
  const [newButtonText, setNewButtonText] = useState('');
  const [newButtonUrl, setNewButtonUrl] = useState('');
  const [newButtonValue, setNewButtonValue] = useState('');
  const [buttonError, setButtonError] = useState('');
  const [buttonTypeCounts, setButtonTypeCounts] = useState({});
  const [editingButtonIndex, setEditingButtonIndex] = useState(-1);
  
  // Check if this is an authentication template with a copy code button
  const isAuthTemplate = buttons.some(button => button.type === 'COPY_CODE' && button.value === '{{1}}');
  
  // Calculate button type counts whenever buttons change
  useEffect(() => {
    const counts = buttons.reduce((acc, button) => {
      const type = button.type || 'reply';
      acc[type] = (acc[type] || 0) + 1;
      return acc;
    }, {});
    setButtonTypeCounts(counts);
  }, [buttons]);
  
  // Reset selected button type if copy code is selected but template is not authentication
  useEffect(() => {
    if (selectedButtonType === 'COPY_CODE' && templateCategory !== 2) {
      setSelectedButtonType('reply');
      if (onButtonTypeChange) {
        onButtonTypeChange('reply');
      }
    }
  }, [templateCategory, selectedButtonType, onButtonTypeChange]);
  
  const handleFooterChange = (e) => {
    setFieldValue('footermessage', e.target.value);
    setFooterText(e.target.value);
  };
  
  const handleButtonTypeChange = (type) => {
    setSelectedButtonType(type);
    setButtonError('');
    if (onButtonTypeChange) {
      onButtonTypeChange(type);
    }
  };
  
  const validateButtonData = () => {
    // Check if button text is empty
    if (!newButtonText.trim()) {
      setButtonError('Button text is required');
      return false;
    }
    
    // Check if URL is required and valid
    if (selectedButtonType === 'URL') {
      if (!newButtonUrl) {
        setButtonError('URL is required');
        return false;
      }
      
      try {
        // Simple URL validation
        new URL(newButtonUrl);
      } catch (err) {
        setButtonError('Please enter a valid URL (include http:// or https://)');
        return false;
      }
    }
    
    // Check if phone number is required and valid
    if (selectedButtonType === 'PHONE_NUMBER') {
      if (!newButtonUrl) {
        setButtonError('Phone number is required');
        return false;
      }
      // Basic phone number validation (starts with + and has at least 8 digits)
      if (!/^[1-9][0-9]{7,14}$/.test(newButtonUrl)) {
        setButtonError('Please enter a valid phone number with country code (e.g., 1234567890)');
        return false;
      }
    }
    
    // Check if copy code is required
    if (selectedButtonType === 'COPY_CODE' && !newButtonValue) {
      setButtonError('Code value is required');
      return false;
    }
    
    // When editing a button, only check limit if changing to a different type
    if (editingButtonIndex >= 0) {
      const currentButton = buttons[editingButtonIndex];
      // If changing to a different button type, check if that type has reached its limit
      if (currentButton && currentButton.type !== selectedButtonType) {
        if (isButtonTypeLimitReached(selectedButtonType)) {
          setButtonError(`Maximum ${BUTTON_TYPE_LIMITS[selectedButtonType]} ${getButtonTypeName(selectedButtonType)} button(s) allowed`);
          return false;
        }
      }
    } else {
      // For new buttons, always check the limit
      if (isButtonTypeLimitReached(selectedButtonType)) {
        setButtonError(`Maximum ${BUTTON_TYPE_LIMITS[selectedButtonType]} ${getButtonTypeName(selectedButtonType)} button(s) allowed`);
        return false;
      }
    }
    
    // Check if we've reached the total button limit (only for new buttons)
    if (editingButtonIndex < 0 && buttons.length >= MAX_TOTAL_BUTTONS) {
      setButtonError(`Maximum ${MAX_TOTAL_BUTTONS} total buttons allowed`);
      return false;
    }
    setButtonError('');
    return true;
  };
  
  const addButton = () => {
    // Validate button data
    if (!validateButtonData()) {
      return;
    }
    
    const newButtonData = {
      type: selectedButtonType,
      text: newButtonText,
    };
    
    // Add URL for link button type
    if (selectedButtonType === 'URL' && newButtonUrl) {
      newButtonData.url = newButtonUrl;
    }
    
    // Add phone number for phone button type
    if (selectedButtonType === 'PHONE_NUMBER' && newButtonUrl) {
      newButtonData.phone_number = newButtonUrl;
    }
    
    // Add value for copy offer code type
    if (selectedButtonType === 'COPY_CODE' && newButtonValue) {
      newButtonData.value = newButtonValue;
    }
    
    if (editingButtonIndex >= 0) {
      // Update existing button
      const newButtons = [...buttons];
      newButtons[editingButtonIndex] = newButtonData;
      setFieldValue('buttons', newButtons);
    } else {
      // Add new button
      const newButtons = [...buttons, newButtonData];
      setFieldValue('buttons', newButtons);
    }
    
    // Reset form
    setNewButtonText('');
    setNewButtonUrl('');
    setNewButtonValue('');
    setShowButtonForm(false);
    setEditingButtonIndex(-1);
  };
  
  const editButton = (index) => {
    const button = buttons[index];
    setSelectedButtonType(button.type);
    setNewButtonText(button.text || '');
    setNewButtonUrl(button.url || button.phone_number || '');
    setNewButtonValue(button.value || '');
    setShowButtonForm(true);
    setEditingButtonIndex(index);
  };
  
  const removeButton = (index) => {
    const newButtons = [...buttons];
    newButtons.splice(index, 1);
    setFieldValue('buttons', newButtons);
    setButtonError('');
  };
  
  const getButtonIcon = (type) => {
    switch(type) {
      case 'URL':
        return faLink;
      case 'PHONE_NUMBER':
        return faPhone;
      case 'COPY_CODE':
        return faCopy;
      default:
        return faComment;
    }
  };
  
  const getButtonTypeName = (type) => {
    switch(type) {
      case 'URL':
        return 'Visit Website';
      case 'PHONE_NUMBER':
        return 'Call Phone';
      case 'COPY_CODE':
        return 'Copy Code';
      default:
        return 'Quick Reply';
    }
  };
  
  // Check if we've reached the limit for a specific button type
  const isButtonTypeLimitReached = (type) => {
    // If we're editing a button, we need to adjust the count
    if (editingButtonIndex >= 0 && buttons[editingButtonIndex]?.type === type) {
      // Subtract 1 from the count since we're replacing this button
      return ((buttonTypeCounts[type] || 0) - 1) >= BUTTON_TYPE_LIMITS[type];
    }
    // Normal check for new buttons
    return (buttonTypeCounts[type] || 0) >= BUTTON_TYPE_LIMITS[type];
  };
  
  // Get remaining count for current button type
  const getRemainingCount = (type) => {
    const limit = BUTTON_TYPE_LIMITS[type] || 0;
    const current = buttonTypeCounts[type] || 0;
    return Math.max(0, limit - current);
  };
  
  // Get total remaining buttons
  const getTotalRemainingCount = () => {
    return Math.max(0, MAX_TOTAL_BUTTONS - buttons.length);
  };
  
  return (
    <div className="mt-4">      
      <div className="form-group">
        <label htmlFor="footerText">Footer Text (Optional)</label>
        <div className="input-group p-0">
          <input 
            type="text" 
            id="footerText"
            className="form-control" 
            placeholder="Add optional footer text" 
            value={footerText}
            onChange={handleFooterChange}
            maxLength={60}
          />
          <div className="input-group-append border d-flex align-items-center px-2">
            <span className='text-dark' style={{fontSize: '12px'}}>{(footerText ? footerText.length : 0)}/60</span>
          </div>
        </div>
        <small className="form-text text-muted">
          Footer appears below your message in smaller text
        </small>
      </div>
      
      {/* For authentication templates, show simplified button UI */}
      {isAuthTemplate ? (
        <div className="form-group mt-3">
          <label>Buttons</label>
          <div className="list-group mb-3">
            {buttons.map((button, index) => (
              <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon 
                    icon={faCopy} 
                    className="mr-2 text-primary" 
                  />
                  <div>
                    <div>{button.text}</div>
                    <small className="text-muted">
                      Copies verification code to clipboard
                    </small>
                  </div>
                </div>
                <div>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline-primary mr-2"
                    onClick={() => editButton(index)}
                  >
                    <FontAwesomeIcon icon={faPencilAlt} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <p className="text-muted small">
            For authentication templates, the Copy Code button is automatically configured to copy the verification code.
          </p>
        </div>
      ) : (
        <div className="form-group mt-3">
          <label>Buttons (Optional) <span className="text-muted small">({buttons.length}/{MAX_TOTAL_BUTTONS} used)</span></label>
          
          <div className="button-type-selector mb-3">
            <div className="d-flex flex-wrap">
              <div className="btn-group btn-group-sm mb-2 mr-2">
                <button
                  type="button"
                  className={`btn ${selectedButtonType === 'reply' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleButtonTypeChange('reply')}
                >
                  <FontAwesomeIcon icon={faComment} className="mr-1" /> 
                  Quick Reply
                  <span className="ml-1 badge badge-light">{getRemainingCount('reply')}/{BUTTON_TYPE_LIMITS.QUICK_REPLY}</span>
                </button>
              </div>
              
              <div className="btn-group btn-group-sm mb-2 mr-2">
                <button
                  type="button"
                  className={`btn ${selectedButtonType === 'URL' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleButtonTypeChange('URL')}
                >
                  <FontAwesomeIcon icon={faLink} className="mr-1" /> 
                  Visit Website
                  <span className="ml-1 badge badge-light">{getRemainingCount('URL')}/{BUTTON_TYPE_LIMITS.URL}</span>
                </button>
              </div>
              
              <div className="btn-group btn-group-sm mb-2 mr-2">
                <button
                  type="button"
                  className={`btn ${selectedButtonType === 'PHONE_NUMBER' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => handleButtonTypeChange('PHONE_NUMBER')}
                >
                  <FontAwesomeIcon icon={faPhone} className="mr-1" /> 
                  Call Phone
                  <span className="ml-1 badge badge-light">{getRemainingCount('PHONE_NUMBER')}/{BUTTON_TYPE_LIMITS.PHONE_NUMBER}</span>
                </button>
              </div>
              
              {/* Only show Copy Code button for authentication templates (category 2) */}
              {templateCategory === 2 && (
                <div className="btn-group btn-group-sm mb-2">
                  <button
                    type="button"
                    className={`btn ${selectedButtonType === 'COPY_CODE' ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => handleButtonTypeChange('COPY_CODE')}
                  >
                    <FontAwesomeIcon icon={faCopy} className="mr-1" /> 
                    Copy Code
                        <span className="ml-1 badge badge-light">{getRemainingCount('COPY_CODE')}/{BUTTON_TYPE_LIMITS.COPY_CODE}</span>
                  </button>
                </div>
              )}
            </div>
            
            <small className="text-muted d-block mt-1">
              {selectedButtonType === 'reply' && 'Quick reply buttons let customers respond with a single tap'}
              {selectedButtonType === 'URL' && 'URL buttons can link to your website or landing page'}
              {selectedButtonType === 'PHONE_NUMBER' && 'Phone buttons let customers call you with one tap'}
              {selectedButtonType === 'COPY_CODE' && templateCategory === 2 && 'Copy Code lets customers easily copy promotional codes'}
            </small>
          </div>
          
          {buttons && buttons.length > 0 && (
            <div className="list-group mb-3">
              {buttons.map((button, index) => (
                <div key={index} className="list-group-item d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <FontAwesomeIcon 
                      icon={getButtonIcon(button.type || selectedButtonType)} 
                      className="mr-2 text-primary" 
                    />
                    <div>
                      <div>{button.text}</div>
                      {button.url && <small className="text-muted">{button.url}</small>}
                      {button.phone_number && <small className="text-muted">{button.phone_number}</small>}
                      {button.value && <small className="text-muted">Code: {button.value}</small>}
                      {!button.url && !button.phone_number && !button.value && button.type && (
                        <small className="text-muted">{getButtonTypeName(button.type)}</small>
                      )}
                    </div>
                  </div>
                  <div>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-primary mr-2"
                      onClick={() => editButton(index)}
                    >
                      <FontAwesomeIcon icon={faPencilAlt} />
                    </button>
                    <button 
                      type="button" 
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => removeButton(index)}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {buttonError && (
            <div className="alert alert-danger py-2" role="alert">
              <small>{buttonError}</small>
            </div>
          )}
          
          {showButtonForm ? (
            <div className="card p-3 mb-3">
              <div className="form-group mb-2">
                <label>Button Text</label>
                <input 
                  type="text" 
                  className="form-control"
                  placeholder="Enter button text" 
                  value={newButtonText}
                  onChange={(e) => setNewButtonText(e.target.value)}
                  maxLength={20}
                  autoFocus
                />
                <small className="form-text text-muted">
                  Maximum 20 characters
                </small>
              </div>
              
              {selectedButtonType === 'URL' && (
                <div className="form-group mb-2">
                  <label>Website URL</label>
                  <input 
                    type="url" 
                    className="form-control"
                    placeholder="https://example.com" 
                    value={newButtonUrl}
                    onChange={(e) => setNewButtonUrl(e.target.value)}
                  />
                  <small className="form-text text-muted">
                    Enter a valid URL including http:// or https://
                  </small>
                </div>
              )}
              
              {selectedButtonType === 'PHONE_NUMBER' && (
                <div className="form-group mb-2">
                  <label>Phone Number</label>
                  <input 
                    type="tel" 
                    className="form-control"
                    placeholder="1234567890" 
                    value={newButtonUrl}
                    onChange={(e) => setNewButtonUrl(e.target.value)}
                  />
                  <small className="form-text text-muted">
                    Enter a phone number with country code (e.g., +1 for US)
                  </small>
                </div>
              )}
              
              {selectedButtonType === 'COPY_CODE' && (
                <div className="form-group mb-2">
                  <label>Offer Code</label>
                  <input 
                    type="text" 
                    className="form-control"
                    placeholder="DISCOUNT50" 
                    value={newButtonValue}
                    onChange={(e) => setNewButtonValue(e.target.value)}
                  />
                  <small className="form-text text-muted">
                    Enter the code customers will copy
                  </small>
                </div>
              )}
              
              <div className="d-flex mt-3">
                <button 
                  type="button" 
                  className="btn btn-primary mr-2"
                  onClick={addButton}
                >
                  <FontAwesomeIcon icon={editingButtonIndex >= 0 ? faPencilAlt : faPlus} className="mr-1" />
                  {editingButtonIndex >= 0 ? 'Update Button' : 'Add Button'}
                </button>
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => {
                    setShowButtonForm(false);
                    setNewButtonText('');
                    setNewButtonUrl('');
                    setNewButtonValue('');
                    setEditingButtonIndex(-1);
                    setButtonError('');
                  }}
                >
                  <FontAwesomeIcon icon={faXmark} className="mr-1" />
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button 
              type="button" 
              className={`btn btn-outline-primary ${buttons.length >= MAX_TOTAL_BUTTONS || isButtonTypeLimitReached(selectedButtonType) ? 'disabled' : ''}`}
              onClick={() => setShowButtonForm(true)}
              disabled={buttons.length >= MAX_TOTAL_BUTTONS || isButtonTypeLimitReached(selectedButtonType)}
            >
              <FontAwesomeIcon icon={faPlus} className="mr-1" />
              {buttons.length >= MAX_TOTAL_BUTTONS ? (
                'Maximum Buttons Reached'
              ) : isButtonTypeLimitReached(selectedButtonType) ? (
                `Maximum ${getButtonTypeName(selectedButtonType)} Buttons Reached`
              ) : (
                `Add ${getButtonTypeName(selectedButtonType)} Button`
              )}
            </button>
          )}
          
          <div className="button-limits-info mt-3 p-2 border rounded bg-light">
            <p className="small mb-1"><strong>Button Limits:</strong></p>
            <ul className="small mb-0 pl-3">
              <li>Quick Reply: up to 3 buttons</li>
              <li>Visit Website: up to 2 buttons</li>
              <li>Call Phone: 1 button maximum</li>
              {templateCategory === 2 && <li>Copy Code: 1 button maximum</li>}
              <li><strong>Total:</strong> Maximum 3 buttons of any type combined</li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}

export default Templatefooter; 